import { gql, useMutation, useQuery } from '@apollo/client';
import { Button, Callout, IconButton, Text, TextField } from '@radix-ui/themes';
import { useRollbar } from '@rollbar/react';
import { jwtDecode } from 'jwt-decode';
import { Eye, EyeOff } from 'lucide-react';
import { Form } from 'radix-ui';
import { use, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { LOGIN_SETTINGS } from '../../../gql';
import { getPasswordValidationMessage, revalidateFields } from '../../../utils/security';
import { setAccessToken } from '../../accesstoken';
import { setExpectedUserId } from '../../sessionIdentity';
import Title from '../../elements/Title';
import LocaleContext from '../../hooks/LocaleContext';
import BackToHubert from '../../navbar/BackToHubert';
import './../../../style/css/password-field.css';

type SubmitData = {
  email: string;
  newPassword?: string;
  firstName: string;
  lastName: string;
};

const ACCOUNT_ACTIVATION = gql`
  mutation ACCOUNT_ACTIVATION($password: String!, $firstName: String!, $lastName: String!, $token: String!) {
    AccountActivationMutation(password: $password, firstName: $firstName, lastName: $lastName, token: $token) {
      accessToken
      user {
        firstName
        lastName
      }
    }
  }
`;

const initState = {
  email: '',
  userId: '',
  tokenError: '',
  password: undefined,
  retypedPassword: '',
  validated: false,
  passwordValidated: false,
  loading: false,
  error: '',
  disabled: false,
  info: undefined,
  showForm: true,
  alreadyActivated: false,
};

function reducer(
  state: {
    tokenError?: string;
    info?: string;
    showForm?: boolean;
    disabled?: boolean;
    password?: string;
    firstName?: string;
    lastName?: string;
    retypedPassword?: string;
    validated?: boolean;
    loading?: boolean;
    error?: string;
    email?: string;
    alreadyActivated?: boolean;
    jwtData?: {
      exp: number;
      user: { firstName: string; lastName: string; email: string };
      client: { org_name: string };
      invitedBy: { firstName: string; company: string };
    };
  },
  action: {
    type: string;
    disabled?: boolean;
    showForm?: boolean;
    password?: string;
    firstName?: string;
    lastName?: string;
    retypedPassword?: string;
    validated?: boolean;
    loading?: boolean;
    error?: string;
    info?: string;
    email?: string;
    alreadyActivated?: boolean;
    jwtData?: {
      exp: number;
      user: { firstName: string; lastName: string; email: string };
      client: { org_name: string };
      invitedBy: { firstName: string; company: string };
    };
  },
) {
  switch (action.type) {
    case 'setDisabled':
      return { ...state, disabled: action.disabled };
    case 'setAlreadyActivated':
      return { ...state, alreadyActivated: action.alreadyActivated };
    case 'setJwt':
      return {
        ...state,
        firstName: action.jwtData?.user.firstName,
        lastName: action.jwtData?.user.lastName,
        email: action.jwtData?.user.email,
        jwtData: action.jwtData,
        exp: action.jwtData ? action.jwtData.exp * 1000 : 0,
      };
    case 'setShowForm':
      return {
        ...state,
        showForm: action.showForm,
      };
    case 'setPassword':
      return {
        ...state,
        password: action.password,
      };
    case 'setFirstName':
      return {
        ...state,
        firstName: action.firstName,
      };
    case 'setLastName':
      return {
        ...state,
        lastName: action.lastName,
      };
    case 'setRetypedPassword':
      return {
        ...state,
        retypedPassword: action.retypedPassword,
      };
    case 'setValidated':
      return {
        ...state,
        validated: action.validated,
      };
    case 'setPasswordValidated':
      return {
        ...state,
        passwordValidated: action.validated,
      };
    case 'setLoading':
      return {
        ...state,
        loading: action.loading,
      };
    case 'setTokenError':
      return {
        ...state,
        tokenError: action.error,
      };
    case 'setInfo':
      return {
        ...state,
        info: action.info,
      };

    default:
      throw new Error(`No ${action.type} is defined`);
  }
}

const Signup = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initState);
  const { setLoggedIn } = use(LocaleContext);
  const rollbar = useRollbar();

  const navigate = useNavigate();
  const query = useUrlQuery();

  const magicLink = query.get('token');
  const [activateAccount] = useMutation(ACCOUNT_ACTIVATION);

  useEffect(() => {
    let jwt:
      | {
          exp: number;
          user: {
            firstName: string;
            lastName: string;
            email: string;
          };
          client: {
            org_name: string;
          };
          invitedBy: {
            firstName: string;
            company: string;
          };
        }
      | false = false;
    if (magicLink) {
      try {
        jwt = jwtDecode(magicLink);
      } catch (err) {
        rollbar.warning('Invalid invitation token could not be decoded', {
          error: err instanceof Error ? err.message : String(err),
          tokenPrefix: magicLink.slice(0, 8),
        });
        jwt = false;
      }
    }
    if (jwt) {
      dispatch({ type: 'setJwt', jwtData: jwt });
      dispatch({ type: 'setTokenError', error: '' });
    } else {
      dispatch({ type: 'setTokenError', error: t('signup.tokenError.invalid') });
    }
    setLoggedIn?.(false);
    setAccessToken('');
  }, []);

  const email = state.email;
  const { loading, data } = useQuery(LOGIN_SETTINGS, { variables: { email }, skip: !email });

  const handleSubmit = (e: { email: string; firstName: string; lastName: string; password: string }) => {
    if (state.loading) return;
    if (e.email && e.firstName && e.lastName && magicLink) {
      dispatch({ type: 'setLoading', loading: true });
      activateAccount({
        variables: {
          password: e.password,
          firstName: e.firstName.trim(),
          lastName: e.lastName.trim(),
          token: magicLink,
        },
      })
        .then((response) => {
          dispatch({ type: 'setDisabled', disabled: true });

          dispatch({ type: 'setPassword', password: '' });
          dispatch({ type: 'setRetypedPassword', retypedPassword: '' });
          dispatch({ type: 'setLoading', loading: false });

          setAccessToken(response.data.AccountActivationMutation.accessToken);
          setExpectedUserId(response.data.AccountActivationMutation.accessToken);
          setLoggedIn?.(true);
          dispatch({ type: 'setInfo', info: t('signup.info.success') });
          dispatch({ type: 'setShowForm', showForm: false });
          setTimeout(() => dispatch({ type: 'setInfo', info: t('signup.info.loggingIn') }), 2000);

          setTimeout(() => (query.get('redirect') ? navigate(query.get('redirect') || '/') : navigate('/')), 5000);
        })
        .catch((err) => {
          console.error(err);
          const code = err.graphQLErrors?.[0]?.extensions?.code;
          if (code === 'AlreadyExists') {
            dispatch({ type: 'setTokenError', error: t('signup.error.alreadyActivated') });
            dispatch({ type: 'setAlreadyActivated', alreadyActivated: true });
          } else {
            dispatch({ type: 'setTokenError', error: t('signup.error.generic') });
          }
          dispatch({ type: 'setPassword', password: '' });
          dispatch({ type: 'setRetypedPassword', retypedPassword: '' });
          dispatch({ type: 'setLoading', loading: false });
        });
    } else dispatch({ type: 'setTokenError', error: t('signup.tokenError.missingFields') });
  };

  const setupForm = () => {
    if (loading) return t('signup.loading');
    return (
      <>
        {state.jwtData && state.showForm && (
          <SetupForm state={{ ...state, loginProvider: data.loginSettings.provider }} handleSubmit={handleSubmit} />
        )}
      </>
    );
  };

  return (
    <div className="vh-100 flex items-center justify-center">
      <BackToHubert />
      <Title title={t('signup.pageTitle')} />
      <div className="w-3/4 pb-12" style={{ maxWidth: '25rem' }}>
        <div className="text-center">
          <img src="/logo/hubert_logo_lockup_dark@2x.png" className="mw-100" alt={t('signup.logo.alt')} />
          <h2 style={{ fontSize: '1.9rem' }}>{t('signup.heading')}</h2>
          {state.tokenError && (
            <Callout.Root color="red">
              <Callout.Text className="mb-0">{state.tokenError}</Callout.Text>
            </Callout.Root>
          )}
          {state.alreadyActivated && (
            <Button
              size="3"
              className="w-full"
              style={{ marginTop: '1rem', marginBottom: '1rem' }}
              onClick={() => navigate('/signin')}
            >
              {t('signIn.button.signIn')}
            </Button>
          )}
          {state.info && (
            <Callout.Root color="green">
              <Callout.Text className="mb-0">{state.info}</Callout.Text>
            </Callout.Root>
          )}
          {state.jwtData &&
            state.showForm &&
            (() => {
              const name = state.jwtData.invitedBy?.firstName;
              const company = state.jwtData.invitedBy?.company;
              const orgName = state.jwtData.client?.org_name;

              let message: string;
              if (name && company) {
                message = t('signup.invitation.byNameAndCompany', { name, company });
              } else if (name) {
                message = t('signup.invitation.byNameOnly', { name });
              } else if (orgName) {
                message = t('signup.invitation.byCompanyOnly', { company: orgName });
              } else {
                message = t('signup.invitation.generic');
              }

              return (
                <Callout.Root color="blue" className="justify-content-center" mt="3" mb="5">
                  <Callout.Text className="mb-0">{message}</Callout.Text>
                </Callout.Root>
              );
            })()}
        </div>
        {setupForm()}
      </div>
    </div>
  );
};

export default Signup;

function useUrlQuery() {
  return new URLSearchParams(useLocation().search);
}

const SetupForm = ({
  state,
  handleSubmit,
}: {
  state: {
    firstName?: string;
    lastName?: string;
    email?: string;
    loginProvider: string;
    loading?: boolean;
  };
  handleSubmit: (e: { email: string; firstName: string; lastName: string; password: string }) => void;
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [invalidNewPasswordMessage, setInvalidNewPasswordMessage] = useState(
    t('signup.validation.invalidPasswordFallback'),
  );
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  return (
    <Form.Root
      onSubmit={(formEvent) => {
        formEvent.preventDefault();

        const form = formEvent.currentTarget;

        revalidateFields([newPasswordRef.current, confirmPasswordRef.current]);

        if (!form.checkValidity()) {
          return;
        }

        const formData = Object.fromEntries(new FormData(form)) as unknown as SubmitData;

        handleSubmit({
          email: formData.email.trim().toLowerCase(),
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          password: formData.newPassword ? formData.newPassword.trim() : '',
        });
      }}
    >
      <Form.Field name="email" className="mb-4">
        <div className="flex items-baseline">
          <Form.Label asChild>
            <Text size="2">
              <strong>{t('signup.form.emailLabel')}</strong>
            </Text>
          </Form.Label>
        </div>
        <div className="flex items-center">
          <Form.Control asChild>
            <TextField.Root className="w-full" size="3" readOnly defaultValue={state.email} />
          </Form.Control>
        </div>
      </Form.Field>
      <Form.Field name="firstName" className="mb-4">
        <div className="flex items-baseline justify-between">
          <Form.Label asChild>
            <Text size="2">
              <strong>{t('signup.form.firstNameLabel')}</strong>
            </Text>
          </Form.Label>
          <Text size="1" color="red">
            <Form.Message match="valueMissing">{t('signup.form.firstNameRequired')}</Form.Message>
          </Text>
        </div>
        <Form.Control asChild>
          <TextField.Root
            placeholder={t('signup.form.firstNamePlaceholder')}
            type="text"
            required
            className="w-full"
            size="3"
            defaultValue={state.firstName}
          />
        </Form.Control>
      </Form.Field>
      <Form.Field name="lastName" className="mb-4">
        <div className="flex items-baseline justify-between">
          <Form.Label asChild>
            <Text size="2">
              <strong>{t('signup.form.lastNameLabel')}</strong>
            </Text>
          </Form.Label>
          <Text size="1" color="red">
            <Form.Message match="valueMissing">{t('signup.form.lastNameRequired')}</Form.Message>
          </Text>
        </div>
        <Form.Control asChild>
          <TextField.Root
            placeholder={t('signup.form.lastNamePlaceholder')}
            type="text"
            required
            className="w-full"
            size="3"
            defaultValue={state.lastName}
          />
        </Form.Control>
      </Form.Field>
      {state.loginProvider === 'password' && (
        <>
          <Form.Field name="newPassword" className="mb-4">
            <div className="flex items-baseline justify-between">
              <Form.Label asChild>
                <Text size="2">
                  <strong>{t('signup.form.newPasswordLabel')}</strong>
                </Text>
              </Form.Label>
              <Text size="1" color="red">
                <Form.Message match="valueMissing">{t('signup.form.newPasswordRequired')}</Form.Message>
                <Form.Message
                  match={(value) => {
                    const message = getPasswordValidationMessage(value, t, 'signup.validation.invalidPasswordFallback');
                    if (message) {
                      setInvalidNewPasswordMessage(message);
                      return true;
                    }
                    return false;
                  }}
                >
                  {invalidNewPasswordMessage}
                </Form.Message>
              </Text>
            </div>
            <div className="flex items-center">
              <Form.Control asChild>
                <TextField.Root
                  ref={newPasswordRef}
                  placeholder={t('signup.form.newPasswordPlaceholder')}
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="w-full"
                  size="3"
                />
              </Form.Control>
              <IconButton
                type="button"
                size="3"
                variant="outline"
                color="gray"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <Eye /> : <EyeOff />}
              </IconButton>
            </div>
          </Form.Field>
          <Form.Field name="confirmPassword" className="mb-4">
            <div className="flex items-baseline justify-between">
              <Form.Label asChild>
                <Text size="2">
                  <strong>{t('signup.form.confirmPasswordLabel')}</strong>
                </Text>
              </Form.Label>
              <Text size="1" color="red">
                <Form.Message match="valueMissing">{t('signup.form.confirmPasswordRequired')}</Form.Message>
                <Form.Message match={(value, formData) => value !== (formData.get('newPassword') as string)}>
                  {t('signup.form.passwordsMustMatch')}
                </Form.Message>
              </Text>
            </div>
            <div className="flex items-center">
              <Form.Control asChild>
                <TextField.Root
                  ref={confirmPasswordRef}
                  placeholder={t('signup.form.confirmPasswordPlaceholder')}
                  type={showPasswordConfirmation ? 'text' : 'password'}
                  required
                  className="w-full"
                  size="3"
                />
              </Form.Control>
              <IconButton
                type="button"
                size="3"
                variant="outline"
                color="gray"
                onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
              >
                {showPasswordConfirmation ? <Eye /> : <EyeOff />}
              </IconButton>
            </div>
          </Form.Field>
        </>
      )}
      <Form.Submit asChild>
        <Button size="3" className="w-full" loading={state.loading} disabled={state.loading}>
          {t('signup.submitButton')}
        </Button>
      </Form.Submit>
    </Form.Root>
  );
};
